import * as Sentry from "@sentry/react";
import {
  FinixPaymentForm,
  IFinixPaymentFormHandle,
} from "components/FinixPaymentForm/FinixPaymentForm";
import { events } from "constants/tagManager";
import { ToastContext } from "context/ToastContext";
import { centsToDollars, parsePrice } from "helpers/price";
import { useAnalytics } from "hooks/useAnalytics";
import useDocumentTitle from "hooks/useDocumentTitle";
import { useFinixFraudSession } from "hooks/useFinixJs";
import { debounce } from "lodash";
import { Button } from "primereact/button";
import {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  useCancelFinixSplitAuthMutation,
  useCaptureFinixSplitPaymentMutation,
  useCreateFinixPaymentMutation,
  useCreateFinixSplitAuthMutation,
  useGetFinixSavedPaymentMethodQuery,
  usePayFinixRemainingWithSavedCardMutation,
} from "store/queries/finix";
import { IOrder } from "types/Order/Order";

import { CardAccordion } from "../PaymentSection/components/CardAccordion";
import { PaymentSplitSection } from "../PaymentSection/components/PaymentSplitSection";
import { SavedCard } from "../PaymentSection/components/SavedCard";
import { PaymentSuccessFull } from "../PaymentSection/PaymentSuccessFull/PaymentSuccessFull";
import {
  ContainerForm,
  StyledButton,
  StyledInputNumber,
  StyledSplitButton,
} from "../PaymentSection/styled";

interface IFinixPaymentSectionProps {
  order: IOrder;
  prices: {
    clearPrice: number;
    totalSaving: number;
    fullPrice: number;
  };
  finixApplicationId: string;
  finixMerchantId?: string;
  finixEnvironment: "sandbox" | "live";
  showPaymentSection: {
    paymentPlan: boolean;
    paymentSection: boolean;
    paymentSuccess: boolean;
  };
  setShowPaymentSection: any;
  onReturnClick?: () => void;
  onUpdateOrder?: () => void;
  isPaymentPlan?: boolean;
  isDownPaymentOnly?: boolean;
  downPaymentAmountInCents?: number;
  /** When true, the user is paying down an existing PaymentPlan's remaining balance.
   *  prices.clearPrice is the current remaining amount. User picks a custom amount
   *  between $1 and clearPrice and either uses a saved Finix PI or enters a new card. */
  payRemainingPrice?: boolean;
}

export function FinixPaymentSection({
  order,
  prices,
  finixApplicationId,
  finixMerchantId,
  finixEnvironment,
  showPaymentSection,
  setShowPaymentSection,
  onReturnClick,
  onUpdateOrder,
  isPaymentPlan = false,
  isDownPaymentOnly = false,
  downPaymentAmountInCents,
  payRemainingPrice = false,
}: IFinixPaymentSectionProps) {
  useDocumentTitle("Payment Page");

  const { pageViewEvents } = useAnalytics();
  const { current: toastElement } = useContext(ToastContext);

  // Initialize Finix.Auth for on-session fraud detection — its getSessionKey() is
  // called right before each auth/transfer mutation so the key is fresh.
  const { getFraudSessionId } = useFinixFraudSession(
    finixEnvironment,
    finixMerchantId
  );

  const firstFormRef = useRef<IFinixPaymentFormHandle>(null);
  const secondFormRef = useRef<IFinixPaymentFormHandle>(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [firstCardComplete, setFirstCardComplete] = useState(false);
  const [secondCardComplete, setSecondCardComplete] = useState(false);

  // Split payment state
  const [isSplitPayment, setIsSplitPayment] = useState(false);
  const [firstCardAmount, setFirstCardAmount] = useState<number>(0);
  const [secondCardAmount, setSecondCardAmount] = useState<number>(0);
  const [preferredPaymentMethodIndex, setPreferredPaymentMethodIndex] =
    useState<0 | 1>(0);

  // Pay-remaining-price state (parity with Stripe PaymentSection)
  const [editableRemainingAmount, setEditableRemainingAmount] =
    useState<number>(payRemainingPrice ? prices.clearPrice : 0);
  const [savedPaymentMethod, setSavedPaymentMethod] = useState<any>(null);
  const [useSavedPaymentMethod, setUseSavedPaymentMethod] = useState(false);

  const {
    data: savedPaymentMethodData,
    isLoading: isSavedPaymentMethodLoading,
  } = useGetFinixSavedPaymentMethodQuery(order.id, {
    skip: !payRemainingPrice,
  });

  useEffect(() => {
    if (savedPaymentMethodData?.data && payRemainingPrice) {
      setSavedPaymentMethod(savedPaymentMethodData.data);
      setUseSavedPaymentMethod(true);
    }
  }, [savedPaymentMethodData, payRemainingPrice]);

  const [payFinixRemainingWithSavedCard] =
    usePayFinixRemainingWithSavedCardMutation();

  // Promise resolvers for tokenization (allow wrapping the callback-based form API)
  const firstCardTokenResolverRef = useRef<{
    resolve: (token: string) => void;
    reject: (err: Error) => void;
  } | null>(null);
  const secondCardTokenResolverRef = useRef<{
    resolve: (token: string) => void;
    reject: (err: Error) => void;
  } | null>(null);

  const [createFinixPayment] = useCreateFinixPaymentMutation();
  const [createFinixSplitAuth] = useCreateFinixSplitAuthMutation();
  const [captureFinixSplitPayment] = useCaptureFinixSplitPaymentMutation();
  const [cancelFinixSplitAuth] = useCancelFinixSplitAuthMutation();

  const publicContact = useCallback(() => {
    const orderData = order as any;
    const provider = orderData?.providers?.[0];
    return {
      isVisible: !!provider,
      name: provider ? `${provider.firstName} ${provider.lastName}` : "",
      role: provider?.specialty || "",
      phone: provider?.phone || "",
    };
  }, [order]);

  const totalAmountInCents = useMemo(() => {
    if (payRemainingPrice) {
      return Math.round(editableRemainingAmount * 100);
    }
    if (isPaymentPlan && downPaymentAmountInCents) {
      return downPaymentAmountInCents;
    }
    return Math.round(prices.clearPrice * 100);
  }, [
    payRemainingPrice,
    editableRemainingAmount,
    isPaymentPlan,
    downPaymentAmountInCents,
    prices.clearPrice,
  ]);

  const totalPaymentAmount = useMemo(
    () => centsToDollars(totalAmountInCents),
    [totalAmountInCents]
  );

  const displayAmount = useMemo(() => {
    if (payRemainingPrice) {
      return prices.clearPrice;
    }
    if (isPaymentPlan && downPaymentAmountInCents) {
      return centsToDollars(downPaymentAmountInCents);
    }
    return prices.clearPrice;
  }, [
    payRemainingPrice,
    isPaymentPlan,
    downPaymentAmountInCents,
    prices.clearPrice,
  ]);

  const headerTitle = useMemo(() => {
    if (payRemainingPrice) {
      return "Remaining amount";
    }
    if (isPaymentPlan) {
      return isDownPaymentOnly ? "Down Payment" : "Initial Payment";
    }
    return "Checkout";
  }, [payRemainingPrice, isPaymentPlan, isDownPaymentOnly]);

  const handleAmountChange = (e: any): void => {
    const value =
      typeof e?.value === "number" ? e.value : parseFloat(e?.target?.value);
    if (!Number.isNaN(value) && value >= 1 && value <= prices.clearPrice) {
      setEditableRemainingAmount(value);
    }
    if (Number.isNaN(value)) {
      setEditableRemainingAmount(1);
    }
  };

  // Keep firstCardAmount in sync with the split
  useEffect(() => {
    if (isSplitPayment) {
      setFirstCardAmount(totalPaymentAmount - secondCardAmount);
    } else {
      setFirstCardAmount(totalPaymentAmount);
    }
  }, [secondCardAmount, totalPaymentAmount, isSplitPayment]);

  const handleAddSecondCard = () => {
    setIsSplitPayment(true);
    const half = Math.floor(totalPaymentAmount / 2);
    setSecondCardAmount(half);
    setFirstCardAmount(totalPaymentAmount - half);
    setSecondCardComplete(false);
    setPreferredPaymentMethodIndex(0);
  };

  const handleRemoveSecondCard = (e: any) => {
    e?.preventDefault();
    e?.stopPropagation();
    setIsSplitPayment(false);
    setSecondCardAmount(0);
    setFirstCardAmount(totalPaymentAmount);
    setSecondCardComplete(false);
    setPreferredPaymentMethodIndex(0);
  };

  const handlePaymentSplitFirstCardChange = (e: { value: number | null }) => {
    const value = e.value ?? 0;
    const clamped = Math.max(1, Math.min(value, totalPaymentAmount - 1));
    setSecondCardAmount(totalPaymentAmount - clamped);
  };

  const handlePaymentSplitSecondCardChange = (e: { value: number | null }) => {
    const value = e.value ?? 0;
    const clamped = Math.max(1, Math.min(value, totalPaymentAmount - 1));
    setSecondCardAmount(clamped);
  };

  const payNowDisabled = useMemo(() => {
    if (payRemainingPrice) {
      // Saved PI path: amount must be valid, nothing else required
      if (useSavedPaymentMethod && savedPaymentMethod) {
        return (
          editableRemainingAmount < 1 ||
          editableRemainingAmount > prices.clearPrice ||
          isProcessing
        );
      }
      // New card path: amount valid + card fields complete
      return (
        !firstCardComplete ||
        editableRemainingAmount < 1 ||
        editableRemainingAmount > prices.clearPrice ||
        isProcessing
      );
    }
    if (isSplitPayment) {
      return (
        !firstCardComplete ||
        !secondCardComplete ||
        secondCardAmount <= 0 ||
        secondCardAmount >= totalPaymentAmount ||
        isProcessing
      );
    }
    return !firstCardComplete || isProcessing;
  }, [
    payRemainingPrice,
    useSavedPaymentMethod,
    savedPaymentMethod,
    editableRemainingAmount,
    prices.clearPrice,
    isSplitPayment,
    firstCardComplete,
    secondCardComplete,
    secondCardAmount,
    totalPaymentAmount,
    isProcessing,
  ]);

  const debouncedPageViewEvents = useMemo(
    () =>
      debounce((orderData: any, event: string) => {
        pageViewEvents(
          {
            accountName: orderData?.accountName,
            orderType: orderData?.orderType,
            communicationMethod: orderData?.communicationMethod,
          },
          event
        );
      }, 500),
    [pageViewEvents]
  );

  // Cancel any pending debounced analytics call on unmount to avoid a leak
  useEffect(
    () => () => debouncedPageViewEvents.cancel(),
    [debouncedPageViewEvents]
  );

  const showError = useCallback(
    (summary: string, detail: string) => {
      toastElement?.show({
        severity: "error",
        summary,
        detail,
        life: 6000,
      });
    },
    [toastElement]
  );

  // === Single-card (non-split) flow — unchanged from before ===

  const handleSingleCardTokenSuccess = async (token: string) => {
    try {
      // Pay-remaining with a new card: backend routes to processFinixPayRemainingPrice
      // when payRemainingPrice=true. savePaymentMethod=true when the user selected the
      // new card as the preferred card for future autopays (index 1) on a partial payment.
      const isPartialRemaining =
        payRemainingPrice && editableRemainingAmount !== prices.clearPrice;
      const savePaymentMethod =
        payRemainingPrice &&
        preferredPaymentMethodIndex === 1 &&
        isPartialRemaining;

      const result = await createFinixPayment({
        orderId: order.id,
        token,
        amountInCents: totalAmountInCents,
        saveForFuture: isPaymentPlan,
        payRemainingPrice,
        savePaymentMethod,
        fraudSessionId: getFraudSessionId(),
      }).unwrap();

      if (result.success) {
        pageViewEvents(
          {
            accountName: order.account?.name,
            orderType: order.orderType,
            communicationMethod: order.patient?.preferredContactMethod,
          },
          events.PURCHASE
        );

        if (onUpdateOrder) {
          onUpdateOrder();
        }

        setShowPaymentSection({
          paymentPlan: false,
          paymentSection: false,
          paymentSuccess: true,
          receiptAvailable: false,
        });
      }
    } catch (error: any) {
      const errorMessage =
        error?.data?.message ||
        error?.data?.messages?.[0] ||
        "Payment processing failed. Please try again.";

      Sentry.captureMessage("Finix payment processing failed", {
        level: "error",
        tags: {
          operation: "finix_payment_frontend",
          orderId: order.id,
        },
        extra: {
          orderId: order.id,
          amountInCents: totalAmountInCents,
          errorMessage,
          rawError: JSON.stringify(error),
        },
      });

      showError("Payment Failed", errorMessage);
    } finally {
      setIsProcessing(false);
    }
  };

  const handleSingleCardTokenError = (error: string) => {
    setIsProcessing(false);
    showError("Card Error", error);
  };

  // === Split-payment flow ===
  // The FinixPaymentForm uses callbacks (onTokenSuccess/onTokenError). To sequence
  // two tokenizations + backend calls, we wire each form's callbacks to a ref-held
  // Promise resolver, then await each tokenization in order.

  const handleFirstCardTokenSuccess = (token: string) => {
    if (isSplitPayment && firstCardTokenResolverRef.current) {
      firstCardTokenResolverRef.current.resolve(token);
      firstCardTokenResolverRef.current = null;
    } else if (isSplitPayment) {
      // Orphan tokenization — resolver was already consumed or never set. Reset UI so user can retry.
      Sentry.captureMessage("Finix split: orphan first-card token", {
        level: "warning",
        tags: { orderId: order.id },
      });
      setIsProcessing(false);
    } else {
      // Not in a split flow — fall through to single-card handling
      handleSingleCardTokenSuccess(token);
    }
  };

  const handleFirstCardTokenError = (error: string) => {
    if (isSplitPayment && firstCardTokenResolverRef.current) {
      firstCardTokenResolverRef.current.reject(new Error(error));
      firstCardTokenResolverRef.current = null;
    } else if (isSplitPayment) {
      Sentry.captureMessage("Finix split: orphan first-card token error", {
        level: "warning",
        tags: { orderId: order.id },
        extra: { error },
      });
      setIsProcessing(false);
      showError("Card Error", error);
    } else {
      handleSingleCardTokenError(error);
    }
  };

  const handleSecondCardTokenSuccess = (token: string) => {
    if (secondCardTokenResolverRef.current) {
      secondCardTokenResolverRef.current.resolve(token);
      secondCardTokenResolverRef.current = null;
    } else {
      Sentry.captureMessage("Finix split: orphan second-card token", {
        level: "warning",
        tags: { orderId: order.id },
      });
      setIsProcessing(false);
    }
  };

  const handleSecondCardTokenError = (error: string) => {
    if (secondCardTokenResolverRef.current) {
      secondCardTokenResolverRef.current.reject(new Error(error));
      secondCardTokenResolverRef.current = null;
    } else {
      Sentry.captureMessage("Finix split: orphan second-card token error", {
        level: "warning",
        tags: { orderId: order.id },
        extra: { error },
      });
      setIsProcessing(false);
      showError("Card Error", error);
    }
  };

  const tokenizeFirstCard = (): Promise<string> =>
    new Promise((resolve, reject) => {
      firstCardTokenResolverRef.current = { resolve, reject };
      if (firstFormRef.current) {
        firstFormRef.current.submit();
      } else {
        firstCardTokenResolverRef.current = null;
        reject(new Error("First card form is not ready"));
      }
    });

  const tokenizeSecondCard = (): Promise<string> =>
    new Promise((resolve, reject) => {
      secondCardTokenResolverRef.current = { resolve, reject };
      if (secondFormRef.current) {
        secondFormRef.current.submit();
      } else {
        secondCardTokenResolverRef.current = null;
        reject(new Error("Second card form is not ready"));
      }
    });

  const handleSplitPayment = async () => {
    const firstAmountCents = Math.round(firstCardAmount * 100);
    const secondAmountCents = Math.round(secondCardAmount * 100);

    let authResponse: {
      success: boolean;
      authorizationIds: [string, string];
      paymentInstrumentIds: [string, string];
      buyerIdentityId: string;
    };

    try {
      // Tokenize both cards sequentially
      const token1 = await tokenizeFirstCard();
      const token2 = await tokenizeSecondCard();

      // Backend authorizes both cards
      authResponse = await createFinixSplitAuth({
        orderId: order.id,
        token1,
        amountInCents1: firstAmountCents,
        token2,
        amountInCents2: secondAmountCents,
        saveForFuture: isPaymentPlan,
        paymentPlan: isPaymentPlan,
        fraudSessionId: getFraudSessionId(),
      }).unwrap();
    } catch (error: any) {
      const failedCard = error?.data?.failedCard;
      const reason = error?.data?.reason || error?.data?.message;
      const errorMessage = failedCard
        ? `Card ${failedCard} could not be authorized${
            reason ? `: ${reason}` : ""
          }. Please try a different card.`
        : error?.data?.message ||
          error?.message ||
          "Payment failed. Please check your card details and try again.";

      Sentry.captureMessage("Finix split payment authorize failed", {
        level: "error",
        tags: { operation: "finix_split_auth_frontend", orderId: order.id },
        extra: {
          orderId: order.id,
          firstAmountCents,
          secondAmountCents,
          errorMessage,
          rawError: JSON.stringify(error),
        },
      });

      showError("Payment Failed", errorMessage);
      setIsProcessing(false);
      return;
    }

    // Capture both authorizations
    try {
      await captureFinixSplitPayment({
        orderId: order.id,
        authorizationIds: authResponse.authorizationIds,
        amounts: [firstAmountCents, secondAmountCents],
        paymentInstrumentIds: authResponse.paymentInstrumentIds,
        paymentPlan: isPaymentPlan,
        preferredPaymentMethodIndex,
        saveForFuture: isPaymentPlan,
      }).unwrap();
    } catch (error: any) {
      // Capture failed — try to clean up the auths we created
      try {
        await cancelFinixSplitAuth({
          orderId: order.id,
          authorizationIds: authResponse.authorizationIds,
          failureReason: "capture_failed",
        }).unwrap();
      } catch (cancelError) {
        Sentry.captureMessage(
          "Failed to cancel Finix split auths after capture failure",
          {
            level: "warning",
            tags: {
              operation: "finix_split_cancel_after_capture_fail",
              orderId: order.id,
            },
            extra: { cancelError: JSON.stringify(cancelError) },
          }
        );
      }

      const errorMessage =
        error?.data?.message ||
        error?.message ||
        "Payment capture failed. Please try again.";

      Sentry.captureMessage("Finix split payment capture failed", {
        level: "error",
        tags: { operation: "finix_split_capture_frontend", orderId: order.id },
        extra: {
          orderId: order.id,
          firstAmountCents,
          secondAmountCents,
          errorMessage,
          rawError: JSON.stringify(error),
        },
      });

      showError("Payment Failed", errorMessage);
      setIsProcessing(false);
      return;
    }

    // Success
    pageViewEvents(
      {
        accountName: order.account?.name,
        orderType: order.orderType,
        communicationMethod: order.patient?.preferredContactMethod,
      },
      events.PURCHASE
    );

    if (onUpdateOrder) {
      onUpdateOrder();
    }

    setShowPaymentSection({
      paymentPlan: false,
      paymentSection: false,
      paymentSuccess: true,
      receiptAvailable: false,
    });
    setIsProcessing(false);
  };

  // === Pay-remaining with saved Finix PI ===
  // No tokenization — backend reuses the PI stored on the active PaymentPlan.
  const handleSavedCardPayRemaining = async () => {
    try {
      await payFinixRemainingWithSavedCard({
        orderId: order.id,
        amountInCents: totalAmountInCents,
        fraudSessionId: getFraudSessionId(),
      }).unwrap();

      pageViewEvents(
        {
          accountName: order.account?.name,
          orderType: order.orderType,
          communicationMethod: order.patient?.preferredContactMethod,
        },
        events.PURCHASE
      );

      if (onUpdateOrder) onUpdateOrder();

      setShowPaymentSection({
        paymentPlan: false,
        paymentSection: false,
        paymentSuccess: true,
        receiptAvailable: false,
      });
    } catch (error: any) {
      const errorMessage =
        error?.data?.message ||
        error?.data?.messages?.[0] ||
        "Payment failed. Please try again or use a different card.";

      Sentry.captureMessage("Finix pay-remaining (saved PI) failed", {
        level: "error",
        tags: {
          operation: "finix_pay_remaining_saved_pi_frontend",
          orderId: order.id,
        },
        extra: {
          orderId: order.id,
          amountInCents: totalAmountInCents,
          errorMessage,
          rawError: JSON.stringify(error),
        },
      });

      showError("Payment Failed", errorMessage);
    } finally {
      setIsProcessing(false);
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setIsProcessing(true);

    debouncedPageViewEvents(
      {
        accountName: order.account?.name,
        orderType: order.orderType,
        communicationMethod: order.patient?.preferredContactMethod,
      },
      events.CLICK_PURCHASE
    );

    // Pay-remaining with saved PI — no tokenization needed
    if (payRemainingPrice && useSavedPaymentMethod && savedPaymentMethod) {
      handleSavedCardPayRemaining().catch((err) => {
        Sentry.captureException(err, {
          tags: {
            operation: "finix_pay_remaining_saved_pi_unhandled",
            orderId: order.id,
          },
        });
        showError(
          "Payment Failed",
          "An unexpected error occurred. Please refresh and try again."
        );
        setIsProcessing(false);
      });
      return;
    }

    if (isSplitPayment) {
      handleSplitPayment().catch((err) => {
        Sentry.captureException(err, {
          tags: { operation: "finix_split_unhandled", orderId: order.id },
        });
        showError(
          "Payment Failed",
          "An unexpected error occurred. Please refresh and try again."
        );
        setIsProcessing(false);
      });
      return;
    }

    // Single-card path — also handles pay-remaining with a new card
    if (firstFormRef.current) {
      firstFormRef.current.submit();
    } else {
      setIsProcessing(false);
      showError("Error", "Card form is not ready. Please try again.");
    }
  };

  const handleTakeToPrivacyPolicy = (e: any) => {
    e.preventDefault();
    window.open("https://www.clearhealthinc.com/privacy-policy");
  };

  const handleTakeToTermsAndConditions = (e: any) => {
    e.preventDefault();
    window.open("https://www.clearhealthinc.com/terms-of-use");
  };

  if (showPaymentSection.paymentSuccess) {
    return (
      <PaymentSuccessFull
        publicContact={publicContact}
        orderExternalId={order.externalId || ""}
        receiptAvailable={false}
      />
    );
  }

  // Stripe parity: a partial pay-remaining (amount < clearPrice) shows the preferred-card radio
  // so the patient can choose whether to switch the saved card to the new card after the charge.
  const isPartialPayRemaining =
    payRemainingPrice && editableRemainingAmount !== prices.clearPrice;

  return (
    <ContainerForm>
      <div className="flex flex-column">
        <span className="title">
          <span>{headerTitle}</span>
          <span>{parsePrice(displayAmount)}</span>
        </span>
      </div>

      {/* Pay-remaining: custom amount input */}
      {payRemainingPrice && (
        <div className="flex flex-column mt-4 mb-4">
          <label htmlFor="custom-amount" className="text-sm mb-1">
            Custom Amount:
          </label>
          <StyledInputNumber
            id="custom-amount"
            value={editableRemainingAmount}
            mode="currency"
            currency="USD"
            locale="en-US"
            min={1}
            max={prices.clearPrice}
            onValueChange={handleAmountChange}
          />
        </div>
      )}

      {/* Pay-remaining: saved card (from PaymentPlan.finixPaymentInstrumentId) */}
      {payRemainingPrice && savedPaymentMethod && (
        <>
          <SavedCard
            savedPaymentMethod={savedPaymentMethod}
            showFooter={!useSavedPaymentMethod && isPartialPayRemaining}
            checked={preferredPaymentMethodIndex === 0}
            onRadioButtonChange={() => setPreferredPaymentMethodIndex(0)}
            radioValue={0}
          />
          {useSavedPaymentMethod && (
            <Button
              className="p-button-text p-0 mb-4"
              onClick={(e) => {
                e.preventDefault();
                setUseSavedPaymentMethod(!useSavedPaymentMethod);
              }}
            >
              <i className="pi pi-plus-circle mr-2" />
              Use a different card
            </Button>
          )}
        </>
      )}

      {/* Pay-remaining with a new (not saved) card — render the Finix form inside a CardAccordion
          that also carries the "use this card for future payments" radio on partial payments. */}
      {payRemainingPrice &&
        !useSavedPaymentMethod &&
        !isSavedPaymentMethodLoading && (
          <div className="sensitive">
            <CardAccordion
              showStylings
              title="New Card Information"
              isFirstCard={false}
              showFooter={!!savedPaymentMethod && isPartialPayRemaining}
              radioValue={1}
              checked={preferredPaymentMethodIndex === 1}
              onRadioButtonChange={() => setPreferredPaymentMethodIndex(1)}
              cardAmount={0}
              handleRemoveSecondCard={
                savedPaymentMethod
                  ? () => {
                      setUseSavedPaymentMethod(!useSavedPaymentMethod);
                      setPreferredPaymentMethodIndex(0);
                    }
                  : undefined
              }
              footerText="Use this card for future payments"
            >
              <FinixPaymentForm
                ref={firstFormRef}
                applicationId={finixApplicationId}
                environment={finixEnvironment}
                onCardComplete={setFirstCardComplete}
                onTokenSuccess={handleFirstCardTokenSuccess}
                onTokenError={handleFirstCardTokenError}
              />
            </CardAccordion>
          </div>
        )}

      {/* Non-pay-remaining: first card — wrapped in CardAccordion when split is active so it gets the
          footer and radio button for selecting the preferred card for autopay.
          (Pay-remaining has its own card-input branch above.) */}
      {!payRemainingPrice &&
        (isSplitPayment ? (
          <div className="sensitive">
            <CardAccordion
              showStylings
              title="First Card"
              isFirstCard
              showFooter={isPaymentPlan && isSplitPayment}
              radioValue={0}
              checked={preferredPaymentMethodIndex === 0}
              onRadioButtonChange={() => setPreferredPaymentMethodIndex(0)}
              cardAmount={firstCardAmount}
            >
              <FinixPaymentForm
                ref={firstFormRef}
                applicationId={finixApplicationId}
                environment={finixEnvironment}
                onCardComplete={setFirstCardComplete}
                onTokenSuccess={handleFirstCardTokenSuccess}
                onTokenError={handleFirstCardTokenError}
              />
            </CardAccordion>
          </div>
        ) : (
          <div className="sensitive mt-4">
            <FinixPaymentForm
              ref={firstFormRef}
              applicationId={finixApplicationId}
              environment={finixEnvironment}
              onCardComplete={setFirstCardComplete}
              onTokenSuccess={handleFirstCardTokenSuccess}
              onTokenError={handleFirstCardTokenError}
            />
          </div>
        ))}

      {/* Add/Remove second card — hidden in pay-remaining mode (split payment is not supported
          for pay-remaining, matching Stripe behavior in PaymentSection.tsx:1287) */}
      {!payRemainingPrice && !isSplitPayment && (
        <StyledSplitButton
          onClick={handleAddSecondCard}
          disabled={isProcessing}
        >
          <i
            className="pi pi-plus-circle"
            style={{ marginRight: "4px", marginTop: "2px" }}
          />
          <div className="flex flex-column ml-2 text-left gap-1">
            <span className="font-semibold text-sm">
              Split payment between two cards
            </span>
            <span className="font-normal text-xs">
              Divide your {parsePrice(displayAmount)} payment across two
              different payment methods
            </span>
          </div>
        </StyledSplitButton>
      )}
      {!payRemainingPrice && isSplitPayment && (
        <>
          <div className="sensitive">
            <CardAccordion
              showStylings
              title="Second Card"
              isFirstCard={false}
              showFooter={isPaymentPlan && isSplitPayment}
              radioValue={1}
              checked={preferredPaymentMethodIndex === 1}
              onRadioButtonChange={() => setPreferredPaymentMethodIndex(1)}
              cardAmount={secondCardAmount}
              handleRemoveSecondCard={handleRemoveSecondCard}
              isProcessing={isProcessing}
            >
              <FinixPaymentForm
                ref={secondFormRef}
                applicationId={finixApplicationId}
                environment={finixEnvironment}
                onCardComplete={setSecondCardComplete}
                onTokenSuccess={handleSecondCardTokenSuccess}
                onTokenError={handleSecondCardTokenError}
              />
            </CardAccordion>
          </div>

          <PaymentSplitSection
            firstCardAmount={firstCardAmount}
            secondCardAmount={secondCardAmount}
            totalPaymentAmount={totalPaymentAmount}
            onFirstCardAmountChange={handlePaymentSplitFirstCardChange}
            onSecondCardAmountChange={handlePaymentSplitSecondCardChange}
          />
        </>
      )}

      <p className="text-sm gray mt-2 text-left font-normal">
        {`By clicking "Pay Now", I confirm that I have read and agree to the`}{" "}
        <Button
          className="p-button-text text-sm p-0 border-none"
          onClick={handleTakeToPrivacyPolicy}
        >
          Privacy Policy
        </Button>{" "}
        and{" "}
        <Button
          className="p-button-text text-sm p-0 border-none"
          onClick={handleTakeToTermsAndConditions}
        >
          Terms and Conditions
        </Button>{" "}
      </p>

      {isPaymentPlan && (
        <p className="text-sm gray mt-2 text-left font-normal">
          I authorize Clear Health to automatically charge the provided credit
          card for the specified (and updated if applicable) amount in the
          Payment Plan for each billing period
        </p>
      )}

      <p className="text-sm gray mt-2 text-center font-normal mt-4">
        <i className="pi pi-lock mr-2 text-sm " />
        All transactions are secure and encrypted
      </p>

      <StyledButton
        type="submit"
        loading={isProcessing}
        data-testid="button-submit-pay-now"
        className="w-full justify-content-center mt-2"
        onClick={handleSubmit}
        disabled={payNowDisabled}
      >
        <span className="font-bold">Pay Now</span>
      </StyledButton>

      {onReturnClick && (
        <div className="flex align-items-center justify-content-center mt-3">
          <Button
            className="p-button-text mb-0 p-button-secondary p-0"
            onClick={onReturnClick}
          >
            <p className="text-sm purple font-semibold">Back to details</p>
          </Button>
        </div>
      )}
    </ContainerForm>
  );
}
